import { ANALYTICS_TYPE, VIEW } from '../enums';
import { Fragment, FunctionalComponent, h } from 'preact';
import { Suspense, lazy } from 'preact/compat';
import Usercentrics, { UI_VARIANT, isPrivacyButtonUI } from '@usercentrics/cmp-browser-sdk';
import { useDeviceState, useMutation } from '../hooks';
import { TAB_VIEW } from '../enums/Tabs';
import { dispatchAnalyticsEvent } from '../modules/helpers';
import { useSettingsContext } from '../contexts/SettingsContext';
import { useState } from 'preact/hooks';
import { useUiContext } from '../contexts/UiContext';

const TCF = lazy(() => import('./TCF'));
const CCPA = lazy(() => import('./Ccpa'));
const Default = lazy(() => import('./Default'));
const PrivacyButton = lazy(() => import('./Buttons/PrivacyButton'));

type Props = {
  acceptAllDefault: () => Promise<void>;
  acceptAllTCF: () => Promise<void>;
  denyAllDefault: () => Promise<void>;
  denyAllTCF: () => Promise<void>;
  onClose: () => Promise<void>;
  onLanguageChange: (language: string) => Promise<void>;
  showSecondLayer: () => void;
  UC: Usercentrics;
};

const View: FunctionalComponent<Props> = ({
  acceptAllDefault,
  acceptAllTCF,
  denyAllDefault,
  denyAllTCF,
  showSecondLayer,
  onClose,
  onLanguageChange,
  UC,
}) => {
  const [toggleCcpaChecked, setToggleCcpaChecked] = useState(false);
  const { ui } = useSettingsContext();
  const {
    abTestVariant,
    clearCategoryPurposeScrollToId,
    clearServiceVendorScrollToId,
    isAmpEnabled,
    isTrackingEnabled,
    setIsInFullScreen,
    setTabView,
    setView,
    uiVariant,
    updateView,
    view,
  } = useUiContext();
  const { isDesktop } = useDeviceState();

  const [enterFullscreenAmp, { loading: enterFullscreenAmpLoading }] = useMutation(UC.enterFullscreenAmp, UC);
  const [, { loading: acceptAllServicesLoading }] = useMutation(UC.acceptAllServices, UC);
  const [, { loading: acceptAllForTCFLoading }] = useMutation(UC.acceptAllForTCF, UC);
  const [, { loading: acceptAllAmpLoading }] = useMutation(UC.acceptAllAmp, UC);
  const [, { loading: denyAllServicesLoading }] = useMutation(UC.denyAllServices, UC);
  const [, { loading: denyAllForTCFLoading }] = useMutation(UC.denyAllForTCF, UC);
  const [, { loading: denyAllAmpLoading }] = useMutation(UC.denyAllAmp, UC);

  /**
   * AMP UI HANDLERS - methods that are called once certain actions are taken by the user when AMP is enabled
   */
  const onFullScreenAmp = (): void => {
    if (enterFullscreenAmpLoading) {
      return;
    }

    enterFullscreenAmp().then(() => {
      updateView(VIEW.SECOND_LAYER);
      setIsInFullScreen(true);
    });
  };

  /**
   * COMMON HANDLERS - These are used despite the variant
   */

  /** This method should always be triggered when clicking on the floating Privacy Button. */
  const onPrivacyButtonClick = (): void => {
    setTabView(TAB_VIEW.CATEGORIES_PURPOSES);
    clearServiceVendorScrollToId();
    clearCategoryPurposeScrollToId();
    updateView(VIEW.SECOND_LAYER);
  };

  /** This method should always be triggered when clicking on the More button (customize) */
  const onCustomize = async (): Promise<void> => {
    if (enterFullscreenAmpLoading) {
      return;
    }

    if (isTrackingEnabled) {
      dispatchAnalyticsEvent(view, ANALYTICS_TYPE.MORE_INFORMATION_LINK, abTestVariant);
    }

    if (isAmpEnabled) {
      await enterFullscreenAmp();
      setIsInFullScreen(true);
    }

    setTabView(TAB_VIEW.CATEGORIES_PURPOSES);

    // TODO check if this eslint rule makes sense for us
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    showSecondLayer();
  };

  /**
   * A method that will navigate to the secondLayer with the vendors tab selected.
   */
  const onShowVendorsHandler = async (): Promise<void> => {
    if (enterFullscreenAmpLoading) {
      return;
    }

    if (isAmpEnabled) {
      await enterFullscreenAmp();
      setIsInFullScreen(true);
    }

    setTabView(TAB_VIEW.SERVICES_VENDORS);
    setView(VIEW.SECOND_LAYER);
  };

  if (view === VIEW.PRIVACY_BUTTON && isPrivacyButtonUI(ui)) {
    return (
      <Suspense fallback={<Fragment />}>
        <PrivacyButton
          iconUrl={ui.iconUrl}
          onClick={onPrivacyButtonClick}
          position={ui.position}
          isMobile={!isDesktop}
        />
      </Suspense>
    );
  }

  if (view === VIEW.NONE) {
    return null;
  }

  switch (uiVariant) {
    case UI_VARIANT.CCPA: {
      return (
        <Suspense fallback={<Fragment />}>
          <CCPA
            onClose={onClose}
            onCustomize={onCustomize}
            onLanguageChange={onLanguageChange}
            setToggleCcpaChecked={setToggleCcpaChecked}
            toggleCcpaChecked={toggleCcpaChecked}
            UC={UC}
          />
        </Suspense>
      );
    }
    case UI_VARIANT.DEFAULT: {
      return (
        <Suspense fallback={<Fragment />}>
          <Default
            onClose={onClose}
            onCustomize={onCustomize}
            onFullScreenAmp={onFullScreenAmp}
            onLanguageChange={onLanguageChange}
            onAcceptAll={acceptAllDefault}
            onDenyAll={denyAllDefault}
            isAcceptDenyAllLoading={acceptAllServicesLoading || denyAllServicesLoading || denyAllAmpLoading}
            UC={UC}
          />
        </Suspense>
      );
    }
    case UI_VARIANT.TCF: {
      return (
        <Suspense fallback={<Fragment />}>
          <TCF
            onCustomize={onCustomize}
            onFullScreenAmp={onFullScreenAmp}
            onLanguageChange={onLanguageChange}
            onShowVendorsHandler={onShowVendorsHandler}
            onAcceptAll={acceptAllTCF}
            onDenyAll={denyAllTCF}
            isAcceptDenyAllLoading={
              denyAllServicesLoading ||
              denyAllForTCFLoading ||
              acceptAllServicesLoading ||
              acceptAllForTCFLoading ||
              acceptAllAmpLoading
            }
            UC={UC}
          />
        </Suspense>
      );
    }
    default:
      return null;
  }
};

export default View;
